import React, { useEffect, useState } from 'react';
import { Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Close';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { fetchFittings } from '../api/fittings';
import {
  attachSystemFitting,
  deleteSystemNote,
  detachSystemFitting,
  fetchSystemFittings,
  fetchSystemNotes,
  fetchSystemVaults,
  saveSystemNote,
  SystemNote,
} from '../api/systems';
import ConsolePanel from './ConsolePanel';
import LcarsRule from './LcarsRule';
import Markdown from './Markdown';
import MarkdownField from './MarkdownField';
import PanelHeader from './PanelHeader';
import ShareScopeSelect from './ShareScopeSelect';
import StatusBadge from './StatusBadge';
import { C, FONT, hexToRgba, lcarsLabelSx } from '../theme/tokens';
import { ShareScope } from '../types';

/**
 * The thing this system's page never had: somewhere to write down what the
 * corp knows about it — POS locations, doctrine notes, "avoid this hole,
 * X lives here" — and have anyone who can see it read it, regardless of
 * which Data Vault (if any) the system happens to be saved in. A vault
 * entry's own notes stay exactly as they were, scoped to why THAT vault
 * cares; this is corp-wide by default, with the same private/corp/alliance/
 * group scoping every other shared thing in this app uses.
 *
 * Paired with linked fittings — a real attach instead of a hand-typed
 * /fitting/<id> in a note — because "what do we fly here" is the other
 * standing question this page never answered on its own.
 */
const SystemGeneralInfoPanel: React.FC<{ solarSystemId: number }> = ({ solarSystemId }) => (
  <Box>
    <PanelHeader label="General Information" accent={C.gold} dense sx={{ mb: 0.75 }} />
    <LcarsRule accent={C.gold} sx={{ mb: 1.5 }} />
    {/* auto-fit rather than a fixed column count: this renders both on the
        system page and inline inside a vault entry, and the tracks let the
        cards reflow to whatever width they actually get instead of being
        told how many columns the viewport implies. (A `compact` prop used to
        force one column for the vault's old 340px sidebar; that rail is
        gone.) */}
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 2,
        alignItems: 'start',
      }}
    >
      <SystemNotesCard solarSystemId={solarSystemId} />
      <SystemFittingsCard solarSystemId={solarSystemId} />
      <SavedInVaultsCard solarSystemId={solarSystemId} />
    </Box>
  </Box>
);

/**
 * "Have we already filed this?" — previously answerable only by opening
 * every vault in turn. Shows the vaults this character can see that hold
 * this system, each with the compartment it's filed under.
 */
const SavedInVaultsCard: React.FC<{ solarSystemId: number }> = ({ solarSystemId }) => {
  const navigate = useNavigate();
  const { data: entries } = useQuery({
    queryKey: ['system-vaults', solarSystemId],
    queryFn: () => fetchSystemVaults(solarSystemId),
  });

  if (!entries) return null;

  return (
    <ConsolePanel title="Saved In" accent={C.wormhole} corners={false}>
      {entries.length === 0 ? (
        <Typography sx={{ color: C.textDim, fontSize: '0.85rem' }}>
          Not saved in any vault you can see.
        </Typography>
      ) : (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {entries.map((e) => (
            <Box key={e.entryId} sx={{ cursor: 'pointer' }} onClick={() => navigate('/vault')}>
              <StatusBadge
                label={`${e.vaultName}${e.isHome ? ' · home' : e.tag ? ` · ${e.tag}` : ''}`}
                accent={e.isHome ? C.wormhole : C.cyan}
              />
            </Box>
          ))}
        </Stack>
      )}
    </ConsolePanel>
  );
};

const SystemNotesCard: React.FC<{ solarSystemId: number }> = ({ solarSystemId }) => {
  const queryClient = useQueryClient();
  const { data: notes } = useQuery({ queryKey: ['system-notes', solarSystemId], queryFn: () => fetchSystemNotes(solarSystemId) });

  const mine = notes?.find((n) => n.isMine) ?? null;
  const others = (notes ?? []).filter((n) => !n.isMine);

  const [body, setBody] = useState(mine?.body ?? '');
  const [scope, setScope] = useState<ShareScope>(mine?.shareScope ?? 'corp');
  const [groupId, setGroupId] = useState<number | null>(mine?.shareGroupId ?? null);

  // Seed local edit state once the account's own note resolves — a ref-once
  // pattern would miss the first successful fetch; syncing on `mine` instead
  // means the form is blank-then-filled rather than needing a loading gate.
  useEffect(() => {
    if (!mine) return;
    setBody(mine.body);
    setScope(mine.shareScope);
    setGroupId(mine.shareGroupId);
  }, [mine?.id, mine?.body, mine?.shareScope, mine?.shareGroupId]); // eslint-disable-line react-hooks/exhaustive-deps

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['system-notes', solarSystemId] });
  const saveMutation = useMutation({
    mutationFn: () => saveSystemNote(solarSystemId, { body, shareScope: scope, shareGroupId: groupId }),
    onSuccess: invalidate,
  });
  const deleteMutation = useMutation({
    mutationFn: () => deleteSystemNote(solarSystemId),
    onSuccess: () => {
      setBody('');
      invalidate();
    },
  });

  const dirty = body !== (mine?.body ?? '') || scope !== (mine?.shareScope ?? 'corp') || groupId !== (mine?.shareGroupId ?? null);

  return (
    <ConsolePanel title="Notes" accent={C.gold} corners={false}>
      <Stack spacing={1.25}>
        {/* Same editor the vault entry's own note uses — the two are read
            side by side, so one of them rendering markdown and the other
            printing the asterisks would be the inconsistency. Saved by the
            explicit button here (this note carries a share scope that is
            chosen alongside it), so onCommit is a no-op. */}
        <MarkdownField
          value={body}
          onChange={setBody}
          onCommit={() => {}}
          minRows={3}
          placeholder="What does the corp need to know about this system?"
        />
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <ShareScopeSelect
            value={scope}
            groupId={groupId}
            onChange={(s, g) => {
              setScope(s);
              setGroupId(g);
            }}
          />
          <Button
            size="small"
            variant="contained"
            color="secondary"
            disabled={!dirty || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
          {mine && (
            <Button size="small" disabled={deleteMutation.isPending} onClick={() => deleteMutation.mutate()} sx={{ color: C.textDim }}>
              Clear
            </Button>
          )}
        </Stack>
        {saveMutation.isSuccess && !dirty && <StatusBadge label="Saved" accent={C.cyan} />}

        {others.length > 0 && (
          <Box sx={{ pt: 0.5 }}>
            <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.66rem'), mb: 0.75 }}>From others</Typography>
            <Stack spacing={1}>
              {others.map((n) => (
                <OtherNote key={n.id} note={n} />
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
    </ConsolePanel>
  );
};

const OtherNote: React.FC<{ note: SystemNote }> = ({ note }) => (
  <Box sx={{ borderLeft: `2px solid ${hexToRgba(C.gold, 0.4)}`, pl: 1.25, py: 0.25 }}>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
      <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.72rem', color: C.text, fontWeight: 600 }}>
        {note.ownerName ?? `#${note.ownerCharacterId}`}
      </Typography>
      <StatusBadge label={note.shareScope} accent={C.textDim} dim />
    </Stack>
    <Markdown text={note.body} dense />
  </Box>
);

const SystemFittingsCard: React.FC<{ solarSystemId: number }> = ({ solarSystemId }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [pickedId, setPickedId] = useState<number | ''>('');

  const { data: linked } = useQuery({
    queryKey: ['system-fittings', solarSystemId],
    queryFn: () => fetchSystemFittings(solarSystemId),
  });
  const { data: ownFittings } = useQuery({ queryKey: ['fittings'], queryFn: fetchFittings });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['system-fittings', solarSystemId] });
  const attachMutation = useMutation({
    mutationFn: (fittingId: number) => attachSystemFitting(solarSystemId, fittingId),
    onSuccess: () => {
      setPickedId('');
      invalidate();
    },
  });
  const detachMutation = useMutation({
    mutationFn: (linkId: number) => detachSystemFitting(solarSystemId, linkId),
    onSuccess: invalidate,
  });

  // Fittings already attached don't clutter the picker a second time.
  const linkedFittingIds = new Set((linked ?? []).map((l) => l.fittingId));
  const attachable = (ownFittings ?? []).filter((f) => !linkedFittingIds.has(f.id));

  // The `minWidth: 0` on both row Stacks below is load-bearing: a row Stack
  // inside a column Stack is a flex item with min-width:auto, so it sizes to
  // its widest child (a long fitting name, the attach select + button) and
  // runs past the Data Vault's 340px sidebar, where it is clipped rather
  // than scrollable. With it, the chip's own maxWidth/ellipsis applies.
  return (
    <ConsolePanel title="Linked Fittings" accent={C.blue} corners={false}>
      <Stack spacing={1.25}>
        {!linked || linked.length === 0 ? (
          <Typography sx={{ color: C.textDim, fontSize: '0.85rem' }}>No fittings linked to this system yet.</Typography>
        ) : (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
            {linked.map((l) => (
              <Stack
                key={l.id}
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{ cursor: 'pointer', minWidth: 0, maxWidth: '100%' }}
                onClick={() => navigate(`/fittings?fit=${l.fittingId}`)}
              >
                <StatusBadge
                  label={`${l.name} — ${l.shipName}${l.isMine ? '' : ` · ${l.ownerName ?? 'shared'}`}`}
                  accent={C.blue}
                  // A hard cap, not 100%: this panel renders both full-width
                  // on the system page and inside the vault's 340px sidebar,
                  // and a percentage resolves against a flex chain that lets
                  // a long fitting name set the width instead of obeying it.
                  maxWidth={250}
                />
                {l.canUnlink && (
                  <Box
                    role="button"
                    aria-label={`Unlink ${l.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      detachMutation.mutate(l.id);
                    }}
                    sx={{ display: 'flex', color: C.textDim, '&:hover': { color: C.red } }}
                  >
                    <DeleteIcon sx={{ fontSize: 13 }} />
                  </Box>
                )}
              </Stack>
            ))}
          </Stack>
        )}

        {attachable.length > 0 && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <TextField
              size="small"
              select
              label="Attach a saved fitting"
              value={pickedId}
              onChange={(e) => setPickedId(Number(e.target.value))}
              sx={{ flex: 1, minWidth: 0 }}
            >
              {attachable.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.name} — {f.shipName}
                  {!f.isMine && ` · ${f.ownerName ?? 'shared'}`}
                </MenuItem>
              ))}
            </TextField>
            <Button
              size="small"
              variant="outlined"
              disabled={!pickedId || attachMutation.isPending}
              onClick={() => pickedId && attachMutation.mutate(pickedId)}
            >
              Attach
            </Button>
          </Stack>
        )}
        {(!ownFittings || ownFittings.length === 0) && (
          <Typography sx={{ color: C.textDim, fontSize: '0.72rem' }}>
            Import a fitting on the Fittings page to attach one here.
          </Typography>
        )}
      </Stack>
    </ConsolePanel>
  );
};

export default SystemGeneralInfoPanel;
